import { useCallback, useEffect, useRef, useState } from "react";
import { getAddress, type Hex } from "viem";
import { mintFeeSchema, netBuySchema, preparationSchema, proofEligibilitySchema, type BootstrapResponse } from "../../../api/schemas";
import { inheritResponseTime, responseNow, responseSecondsRemaining } from "../../../api/response-time";
import { mintProofKey, validatePreparedMintCalldata } from "../../../contracts/pons-reward-nft";
import { parseUnsignedDecimal } from "../../../lib/financial";
import type { MiningController } from "../../../mining-engine/index";

import type { WalletSessionController } from "../../../wallet/wallet-session";
import { matchesTransactionScope, transactionUnresolved, type PreparedTransaction, type TransactionContext, type TransactionTracker } from "../../../wallet/transaction-tracker";
import { ContextChangedError, type WalletOperation } from "../../../wallet/wallet-operation";
import type { MintReviewView } from "../types";
import { sameAddress } from "../../../state/model-helpers";
import { mintPreparationPermission, preparedMintPermission, type MintDisplayState } from "../../../state/transaction-capability";
import type { ActionContext } from "../../../state/action-context";
import { assertProofForPreparedMint, proofContextMatches, proofLifecycleKey, proofPreparationDecision } from "../../../state/mint-eligibility";
import { isElysium } from "../../../config/elysium";

type Options = ActionContext & {
  walletController: WalletSessionController;
  miningController: MiningController;
  bootstrap: BootstrapResponse | null;
  mintState: MintDisplayState | null;
  activeToken: BootstrapResponse["tokens"][number] | null;

  tracker: TransactionTracker;
  transactionEnvironment: string;
};

export function useMintNft({ walletController, miningController, bootstrap, mintState, activeToken,
  tracker, transactionEnvironment, contextKey, readContextKey,
  beginOperation, endOperation }: Options) {
  const latestBootstrap = useRef(bootstrap);
  latestBootstrap.current = bootstrap;
  // Signed payloads remain in memory only, attached to the original operation lease.
  const pendingReview = useRef<{ view: MintReviewView; operation: WalletOperation;
    call: PreparedTransaction; context: TransactionContext; challengeId: string; netBuyWei: string | null } | null>(null);
  const preparing = useRef<WalletOperation | null>(null);
  const proofGuards = useRef(new Map<WalletOperation, () => void>());
  const finishOperation = useCallback((operation: WalletOperation) => {
    proofGuards.current.get(operation)?.();
    proofGuards.current.delete(operation);
    endOperation(operation);
  }, [endOperation]);
  const proofId = miningController.getSnapshot().proof?.proofId;
  const challengeId = miningController.getSnapshot().challenge?.challengeId;
  const proofStatus = miningController.getSnapshot().proof?.proofStatus;
  const [review, setReview] = useState<MintReviewView | null>(null);
  const cancelMintReview = useCallback(() => {
    for (const operation of [pendingReview.current?.operation, preparing.current]) {
      if (operation) { operation.cancel(); finishOperation(operation); }
    }
    pendingReview.current = null;
    preparing.current = null;
    setReview(null);
  }, [finishOperation]);
  useEffect(() => () => { cancelMintReview(); }, [cancelMintReview, contextKey, proofId, challengeId, proofStatus]);

  const prepareMint = useCallback(async (tierUsd: number) => {
    if (preparing.current) throw new Error("Mint preparation is already in progress");
    cancelMintReview();
    if (contextKey !== readContextKey()) throw new ContextChangedError();
    const { proof, challenge } = miningController.getSnapshot();
    const account = walletController.getAccount();
    const provider = walletController.getProvider();
    if (!bootstrap || !proof || !challenge || proof.proofStatus !== "VERIFIED" ||
      proof.challengeId.toLowerCase() !== challenge.challengeId.toLowerCase()) throw new Error("A verified proof and its challenge are required");
    if (!account || !provider || !activeToken) throw new Error("Connect and authenticate the proof owner wallet");
    if (!bootstrap.contracts.collectionAddress || !bootstrap.contracts.mintChainId) {
      throw new Error("NFT collection is not configured");
    }
    const collection = bootstrap.contracts.collectionAddress;
    const mintPermission = mintPreparationPermission(bootstrap, mintState);
    if (mintPermission.state !== "ALLOWED") throw new Error(mintPermission.reasons.join(", "));
    if (!walletController.getSnapshot().authenticated) throw new Error("Sign in before preparing a mint");
    if (!sameAddress(challenge.walletAddress, account) || !sameAddress(challenge.tokenAddress, activeToken.tokenAddress) ||
      BigInt(challenge.chainId) !== BigInt(bootstrap.sourceChainId)) throw new Error("The proof challenge belongs to another context");
    const transactionContext: TransactionContext = { kind: "MINT", account,
      chainId: bootstrap.contracts.mintChainId, collection, sourceToken: activeToken.tokenAddress,
      tokenId: null, environment: transactionEnvironment };
    if (tracker.getSnapshot().items.some((item) => matchesTransactionScope(item, transactionContext) && transactionUnresolved(item))) {
      throw new Error("Check the existing transaction before preparing another mint");
    }
    const operation = beginOperation();
    preparing.current = operation;
    const capturedProofKey = proofLifecycleKey(miningController.getSnapshot());
    proofGuards.current.set(operation, miningController.subscribe(() => {
      if (proofLifecycleKey(miningController.getSnapshot()) === capturedProofKey) return;
      if (pendingReview.current?.operation === operation || preparing.current === operation) cancelMintReview();
      else operation.cancel();
    }));
    const assertCapturedProof = () => {
      operation.assertCurrent();
      const current = miningController.getSnapshot();
      if (current.proof?.proofId !== proof.proofId || current.challenge?.challengeId !== challenge.challengeId ||
        !proofContextMatches(bootstrap, current, walletController.getSnapshot().authenticated, walletController.getAccount(), activeToken)) {
        operation.cancel();
        throw new ContextChangedError();
      }
    };
    try {
        const [freshMintState, eligibility] = await Promise.all([
          isElysium(bootstrap) || mintState?.providerCheckMode === "ON_PREPARE" ?
            walletController.api.get("/mint/fee", mintFeeSchema, operation.signal) : Promise.resolve(bootstrap.mintState),
          walletController.api.get(`/mining/proofs/${proof.proofId}/eligibility`, proofEligibilitySchema, operation.signal),
        ]);
        assertCapturedProof();
        const freshPermission = mintPreparationPermission(bootstrap, freshMintState);
        if (freshPermission.state !== "ALLOWED") throw new Error(freshPermission.reasons.join(", "));
        if (eligibility.proofId.toLowerCase() !== proof.proofId.toLowerCase() ||
          eligibility.challengeId.toLowerCase() !== challenge.challengeId.toLowerCase()) {
          throw new Error("The eligibility response does not match the verified proof");
        }
        const proofContext = { proofId: proof.proofId, challengeId: challenge.challengeId, matches: true, permission: freshPermission };
        const decision = proofPreparationDecision(eligibility, proofContext, tierUsd);
        if (decision.state !== "ALLOWED") throw new Error(`${decision.message} ${decision.reasons.join(", ")}`);
        const preparation = await walletController.api.post("/mint/prepare", {
          powProofId: proof.proofId,
          selectedTierUsd: tierUsd,
        }, preparationSchema, operation.signal);
        assertCapturedProof();
        assertProofForPreparedMint(eligibility, proofContext, tierUsd);
        const permission = preparedMintPermission(bootstrap, preparation, freshMintState);
        if (permission.state !== "ALLOWED") throw new Error(permission.reasons.join(", "));
        if (!preparation.call.sendable || !preparation.call.contractAddress || !preparation.call.calldata ||
          preparation.call.valueWei === null) throw new Error(preparation.call.reason ?? "Mint call is not sendable");
        if (preparation.powProofId.toLowerCase() !== proof.proofId.toLowerCase()) {
          throw new Error("Prepared transaction references a different proof");
        }
        if (bootstrap.contracts.mintChainId === null || preparation.chainId !== bootstrap.contracts.mintChainId ||
          preparation.sourceChainId !== bootstrap.sourceChainId || preparation.call.mode !== bootstrap.capabilities.mintGatewayMode) {
          throw new Error("Prepared transaction environment does not match the server bootstrap");
        }
        if (preparation.calldata !== preparation.call.calldata ||
          !sameAddress(preparation.contractAddress, preparation.call.contractAddress)) {
          throw new Error("Prepared transaction payload fields disagree");
        }
        if (!sameAddress(preparation.wallet, account) || !sameAddress(preparation.sourceToken, activeToken.tokenAddress) ||
          preparation.selectedTierUsd !== tierUsd || !sameAddress(preparation.call.contractAddress,
            preparation.contractAddress ?? "")) throw new Error("Prepared transaction identity mismatch");
        if (bootstrap.contracts.collectionAddress && !sameAddress(preparation.call.contractAddress,
          bootstrap.contracts.collectionAddress)) throw new Error("Prepared collection is not the configured collection");
        const valueWei = parseUnsignedDecimal(preparation.call.valueWei, "mint value");
        const feeWei = parseUnsignedDecimal(preparation.feeQuoteWei, "mint fee");
        if (valueWei !== feeWei) throw new Error("Prepared transaction value does not match the quote");
        if (isElysium(bootstrap) && preparation.eligibilitySnapshot === undefined)
          throw new Error("The authorized Net Buy snapshot is unavailable. Refresh and prepare a new quote.");
        if (!preparation.authorization) throw new Error("The prepared mint has no backend authorization");
        const proofKey = mintProofKey({ instanceId: challenge.instanceId, chainId: BigInt(challenge.chainId),
          challengeId: challenge.challengeId, challengeCommitment: challenge.challengeCommitment });
        if (proofKey.toLowerCase() !== preparation.proofKey.toLowerCase()) throw new Error("Prepared proof key does not match the verified challenge");
        if (preparation.deadline > eligibility.expiresAt) throw new Error("Mint authorization outlives its proof");
        validatePreparedMintCalldata(preparation.call.calldata as Hex, preparation.authorization, {
          wallet: getAddress(account), sourceToken: getAddress(activeToken.tokenAddress), selectedTierUsd: BigInt(tierUsd),
          quotedMintFeeWei: feeWei, deadline: BigInt(preparation.deadline),
          chainId: preparation.chainId, collection,
          signer: bootstrap.contracts.authorizationSignerAddress, authorizationId: preparation.authorizationId,
          proofKey, instanceId: challenge.instanceId, challengeCommitment: challenge.challengeCommitment,
        });
        assertMintDeadline(preparation);
        const view: MintReviewView = { id: crypto.randomUUID(), contextKey, proofId: proof.proofId,
          tierUsd, account, sourceToken: activeToken.tokenAddress, chainId: preparation.chainId,
          collection, feeWei: feeWei.toString(), deadline: preparation.deadline };
        inheritResponseTime(preparation, view);
        pendingReview.current = { view, operation, challengeId: challenge.challengeId,
          netBuyWei: preparation.eligibilitySnapshot?.netBuyWei ?? null,
          context: { ...transactionContext, mintIdentity: { proofKey, authorizationId: preparation.authorizationId,
            tierUsd: String(tierUsd), feeWei: feeWei.toString() } }, call: {
          account: getAddress(account), chainId: preparation.chainId, to: getAddress(preparation.call.contractAddress),
          data: preparation.call.calldata as Hex, valueWei,
          mintSigner: preparation.authorization.signerAddress,
          ...(isElysium(bootstrap) ? { mintBootstrap: () => latestBootstrap.current } : {}),
          walletMintFee: preparation.call.mode === "MAINNET_READ_ONLY",
        } };
        setReview(view);
    } catch (error) {
      operation.cancel();
      finishOperation(operation);
      throw error;
    }
    finally { if (preparing.current === operation) preparing.current = null; }
  }, [walletController, miningController, activeToken, bootstrap, mintState, tracker, transactionEnvironment, beginOperation, finishOperation, contextKey, readContextKey, cancelMintReview]);

  const confirmMint = useCallback(async (reviewId: string) => {
    const pending = pendingReview.current;
    if (!pending || pending.view.id !== reviewId) throw new Error("Prepare and review a mint quote first.");
    // Consume this review before any await; double clicks cannot request two sends.
    pendingReview.current = null;
    setReview(null);
    const { operation, view, call, context } = pending;
    const assertReviewedContext = () => {
      operation.assertCurrent();
      const current = miningController.getSnapshot();
      if (current.proof?.proofId !== view.proofId || current.proof.proofStatus !== "VERIFIED" ||
        current.challenge?.challengeId !== pending.challengeId) {
        throw new Error("The proof changed. Prepare and review again.");
      }
      assertMintDeadline(view);
    };
    try {
      assertReviewedContext();
      const provider = walletController.getProvider();
      if (!provider) throw new ContextChangedError();
      const result = await tracker.execute(context, provider, async () => {
        operation.assertCurrent();
        const [eligibility, fee, netBuy] = await Promise.all([
          walletController.api.get(`/mining/proofs/${view.proofId}/eligibility`, proofEligibilitySchema, operation.signal),
          call.mintBootstrap ? walletController.api.get("/mint/fee", mintFeeSchema, operation.signal) : Promise.resolve(null),
          call.mintBootstrap ? walletController.api.get(`/tokens/${view.sourceToken}/wallets/${view.account}/net-buy`, netBuySchema, operation.signal) : Promise.resolve(null),
        ]);
        operation.assertCurrent();
        const tierFees: Record<string, string> | undefined = fee?.tierMintFeesWei;
        if (call.mintBootstrap && (!fee?.available || mintPreparationPermission(latestBootstrap.current, fee).state !== "ALLOWED" ||
          !sameAddress(fee.contractAddress, view.collection) || tierFees?.[String(view.tierUsd)] !== view.feeWei ||
          !netBuy || netBuy.chainId !== bootstrap?.sourceChainId || !sameAddress(netBuy.wallet, view.account) ||
          !sameAddress(netBuy.tokenAddress, view.sourceToken) || netBuy.netBuyWei !== pending.netBuyWei ||
          !netBuy.eligibilityAvailable || !netBuy.eligibleTiers?.some(tier => tier === view.tierUsd)))
          throw new Error("Mint fee or Net Buy changed. Prepare and review a new quote; no transaction was sent.");
        if (eligibility.proofId.toLowerCase() !== view.proofId.toLowerCase() ||
          eligibility.challengeId.toLowerCase() !== pending.challengeId.toLowerCase()) {
          throw new Error("Proof or tier eligibility changed. Prepare and review again.");
        }
        assertProofForPreparedMint(eligibility, { proofId: view.proofId, challengeId: pending.challengeId, matches: true }, view.tierUsd);
        if (view.deadline > eligibility.expiresAt) throw new Error("Mint authorization outlives its proof");
        assertMintDeadline(view);
        await operation.switchTo(call.chainId);
        operation.assertCurrent();
        return call;
      }, { signal: operation.signal, assertCurrent: assertReviewedContext });
      // Tracking a failure is not a successful confirmation. Keep the error
      // visible in the review instead of letting App close it as if sent.
      if (!result.hash && ["FAILED", "CANCELLED", "REJECTED", "UNCONFIRMED"].includes(result.phase)) {
        throw new Error(result.message);
      }
    } finally { finishOperation(operation); }
  }, [walletController, miningController, tracker, finishOperation, bootstrap?.sourceChainId]);

  return { prepareMint, confirmMint, cancelMintReview,
    mintReview: review?.contextKey === contextKey && review.proofId === proofId ? review : null };
}

function assertMintDeadline(quote: { deadline: number }): void {
  if (responseNow(quote) === null) throw new Error("Cannot verify the quote expiry. Prepare and review again.");
  if (responseSecondsRemaining(quote, quote.deadline) === 0) {
    throw new Error("AuthorizationExpired: Prepare and review a new quote.");
  }
}
